import React from "react";

/**
 * Brand glyphs — single-colour SVG paths.
 *
 * Each glyph is rendered with `currentColor` so the parent link can
 * theme it via Tailwind's text utilities (we use white-70 default,
 * brand-coloured on hover). The paths are simplified single-glyph
 * versions of the public marks (Instagram camera, X bird, YouTube
 * play, TikTok note, Facebook F) — close enough to be unmistakable
 * but small enough to inline.
 */
const PROPS = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "currentColor",
  "aria-hidden": true,
  focusable: "false",
};

export function InstagramGlyph(props) {
  return (
    <svg {...PROPS} {...props}>
      <path d="M12 2.2c3.2 0 3.6 0 4.8.1 1.2 0 1.8.3 2.2.5.6.2 1 .5 1.4.9.4.4.7.9.9 1.4.2.4.4 1 .5 2.2.1 1.2.1 1.6.1 4.8s0 3.6-.1 4.8c0 1.2-.3 1.8-.5 2.2a3.7 3.7 0 0 1-.9 1.4c-.4.4-.9.7-1.4.9-.4.2-1 .4-2.2.5-1.2.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-1.2 0-1.8-.3-2.2-.5a3.7 3.7 0 0 1-1.4-.9 3.7 3.7 0 0 1-.9-1.4c-.2-.4-.4-1-.5-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.8c0-1.2.3-1.8.5-2.2.2-.6.5-1 .9-1.4.4-.4.9-.7 1.4-.9.4-.2 1-.4 2.2-.5C8.4 2.2 8.8 2.2 12 2.2Zm0 1.8c-3.1 0-3.5 0-4.7.1-1 .1-1.6.2-2 .4-.5.2-.8.4-1.2.8s-.6.7-.8 1.2c-.2.4-.3 1-.4 2C3 8.5 3 8.9 3 12s0 3.5.1 4.7c.1 1 .2 1.6.4 2 .2.5.4.8.8 1.2s.7.6 1.2.8c.4.2 1 .3 2 .4 1.2.1 1.6.1 4.7.1s3.5 0 4.7-.1c1-.1 1.6-.2 2-.4.5-.2.8-.4 1.2-.8s.6-.7.8-1.2c.2-.4.3-1 .4-2 .1-1.2.1-1.6.1-4.7s0-3.5-.1-4.7c-.1-1-.2-1.6-.4-2-.2-.5-.4-.8-.8-1.2s-.7-.6-1.2-.8c-.4-.2-1-.3-2-.4-1.2-.1-1.6-.1-4.7-.1Zm0 3.1a4.9 4.9 0 1 1 0 9.8 4.9 4.9 0 0 1 0-9.8Zm0 1.8a3.1 3.1 0 1 0 0 6.2 3.1 3.1 0 0 0 0-6.2Zm5.1-2.1a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2Z"/>
    </svg>
  );
}

export function TwitterGlyph(props) {
  // The 2023 "X" mark — two crossed strokes inside the bounding box.
  return (
    <svg {...PROPS} {...props}>
      <path d="M18.244 2H21.5l-7.5 8.57L23 22h-6.91l-4.74-6.23L5.7 22H2.444l8.04-9.19L1.5 2h7.07l4.27 5.66L18.244 2Zm-1.21 18.18h1.9L7.08 3.74H5.05L17.034 20.18Z"/>
    </svg>
  );
}

export function YouTubeGlyph(props) {
  return (
    <svg {...PROPS} {...props}>
      <path d="M23.5 7.1a3 3 0 0 0-2.1-2.1C19.5 4.5 12 4.5 12 4.5s-7.5 0-9.4.5A3 3 0 0 0 .5 7.1C0 9 0 12 0 12s0 3 .5 4.9a3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1c.5-1.9.5-4.9.5-4.9s0-3-.5-4.9ZM9.6 15.5V8.5l6.3 3.5-6.3 3.5Z"/>
    </svg>
  );
}

export function TikTokGlyph(props) {
  return (
    <svg {...PROPS} {...props}>
      <path d="M16.6 2v3.2a5.6 5.6 0 0 0 4.4 2.5v3.3a8.7 8.7 0 0 1-4.4-1.4v6.5a6.9 6.9 0 1 1-6.9-6.9c.3 0 .6 0 .9.1v3.4a3.6 3.6 0 1 0 2.6 3.4V2Z"/>
    </svg>
  );
}

export function FacebookGlyph(props) {
  return (
    <svg {...PROPS} {...props}>
      <path d="M22 12a10 10 0 1 0-11.6 9.9V14.9H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.3.2 2.3.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.5 2.9h-2.3v7A10 10 0 0 0 22 12Z"/>
    </svg>
  );
}

/** Lookup: name → React component. */
export const SOCIAL_GLYPHS = {
  instagram: InstagramGlyph,
  twitter:   TwitterGlyph,
  youtube:   YouTubeGlyph,
  tiktok:    TikTokGlyph,
  facebook:  FacebookGlyph,
};

/** Hover-colour token per brand — used as `--brand` so each link can
 * tint towards its own colour on hover without using `box-shadow`
 * glows (per the no-neon rule on the landing). */
export const SOCIAL_HOVER_COLOR = {
  instagram: "#e1306c",
  twitter:   "#ffffff",
  youtube:   "#ff0000",
  tiktok:    "#25f4ee",
  facebook:  "#1877f2",
};
